/*eslint  @typescript-eslint/no-use-before-define: 0*/

import { defineModule } from "direct-vuex";
import { moduleActionContext, moduleGetterContext } from "@/store";
import { CanIssueReceipt } from "@/models/api/orders/CanIssueReceipt";
import { checkRequireDataToGenerateDocumentOrderList } from "@/requests/order/order";
import { createDocument, reprintDocument } from "@/requests/document";
import { ConfirmModel } from "@/models/components/popup/Confirm";
import { filterCapitalize } from "@/filters/capitalize";
import i18n from "@/plugins/i18n";
import { Resolver } from "@/models/components/popup/Resolver";
import OrderDocumentListItem from "@/models/api/orders/OrderDocumentListItem";
import { CashDocumentItemEdit, CreateDocumentCashDocumentModel } from "@/models/api/finances/CashDocumentEdit";
import dayjs from "dayjs";
import { DEFAULT_DATE_FORMAT } from "@/static/dateFormats";
import { DOCUMENT_TYPES } from "@/static/documentTypes";
import { CreateDocumentResponse } from "@/models/api/documents/CreateDocumentResponse";
import { DoubleConfirm } from "@/models/api/DoubleConfirm";

export interface OrderCreateDocumentStateInterface {
  orderId: null | string;
  canIssueReceipt: null | CanIssueReceipt;
  settleWholeDocument: boolean;
  printInvoice: boolean;
  printReceipt: boolean;
  printerId: null | number;
  receiptTypeDialogResolver: null | Resolver;
  kpKwDialogResolver: null | Resolver;
  kpKwModelsAfterDocumentCreate: CreateDocumentCashDocumentModel[];
}

export const OrderCreateDocumentState: OrderCreateDocumentStateInterface = {
  orderId: null,
  canIssueReceipt: null,
  settleWholeDocument: false,
  printInvoice: false,
  printReceipt: false,
  printerId: null,
  receiptTypeDialogResolver: null,
  kpKwDialogResolver: null,
  kpKwModelsAfterDocumentCreate: [],
};

const OrderCreateDocumentModule = defineModule({
  state: OrderCreateDocumentState as OrderCreateDocumentStateInterface,
  namespaced: true as const,
  getters: {},
  mutations: {
    setOrderId(state, param: string | null) {
      state.orderId = param;
    },
    setCanIssueReceipt(state, param: CanIssueReceipt | null) {
      state.canIssueReceipt = param;
    },
    setDefaults(state) {
      state.orderId = null;
      state.canIssueReceipt = null;
      state.settleWholeDocument = false;
      state.printInvoice = false;
      state.printReceipt = false;
      state.printerId = null;
      state.receiptTypeDialogResolver = null;
    },
    setSettleWholeDocument(state, param: boolean) {
      state.settleWholeDocument = param;
    },
    setPrintInvoice(state, param: boolean) {
      state.printInvoice = param;
    },
    setPrintReceipt(state, param: boolean) {
      state.printReceipt = param;
    },
    setPrinterId(state, param: null | number) {
      state.printerId = param;
    },
    setReceiptTypeDialogResolver(state, param: null | Resolver) {
      state.receiptTypeDialogResolver = param;
    },
    setKpKwDialogResolver(state, param: null | Resolver) {
      state.kpKwDialogResolver = param;
    },
    setKpKwModelsAfterDocumentCreate(state, param: CreateDocumentCashDocumentModel[]) {
      state.kpKwModelsAfterDocumentCreate = param;
    },
  },
  actions: {
    async issueDocument(context, params: { orderId: string; timestamp: string | null }) {
      const { commit, rootDispatch, dispatch, state, rootState } = _moduleActionContext(context);
      commit.setDefaults();
      commit.setOrderId(params.orderId);
      const canReceiptIssue = await checkRequireDataToGenerateDocumentOrderList(params.orderId, params.timestamp);
      if (canReceiptIssue.showCanSettleAllWindow) {
        //Czy rozliczyć dokument w całości?
        await dispatch.askUserSettleWholeDocument();
      }

      if (canReceiptIssue.showReceiptWindow) {
        //OKNO DO WYBORU DRUKAREK, PARAGON, FAKTURA, PARAGON I FAKTURA
        await dispatch.askUserReceiptType();
      } else {
        commit.setPrintInvoice(true);
      }

      const documentResponse = await createDocument(params.orderId, {
        // w starym xturze jest taka linijka
        // self.addPaymentAfterGenerate(canIssueReceiptResult.AddPaymentAfterGenerate);
        // flaga otrzymywana w requeście check cośtam... ale ta wartość nie jest później wykorzystywana w żadnym miejscu
        // zamiast tego przypisywana jest odpowiedź z dialogu "Czy rozliczyć dokument w całości"
        // początkowo myślałem, że ma tu być przepisane "addPaymentAfterGenerate"
        createPayments: state.settleWholeDocument, // canReceiptIssue.addPaymentAfterGenerate,
        printInvoice: state.printInvoice,
        printProformaInvoice: false, //ta flaga używana jest w innym miejscu (lista zamówień, dolna zakładka dokumenty)
        printProformaInvoiceGroup: false,
        printReceipt: state.printReceipt,
        printerId: state.printerId,
      });
      await rootDispatch.popup.addSavedOkSnackbar();

      if (canReceiptIssue.showCashDocumentWindow) {
        await dispatch.createKPModelsFromDocument(documentResponse);
        if (state.kpKwModelsAfterDocumentCreate.length > 0)
          await new Promise<null>((resolve, reject) => {
            commit.setKpKwDialogResolver({
              resolver: resolve,
              rejecter: reject,
            } as Resolver);
          });
        commit.setKpKwDialogResolver(null);
        //   // w starym xturze jakaś magia z todo do przebudowy
        //   // window.setAndShowModalWindow('add', 'add-cash-document-window', 'Dodaj KP', '1000', 'default', '32');
        //throw new ErrorHiddenFromConsole("Open KP window and redirect to order list after created/cancelled KP");
      }

      // noinspection ES6MissingAwait
      rootDispatch.orders.getOrdersTable();

      if (rootState.user.showDocumentsAfterGeneration)
        rootDispatch.mails.openNotificationDialog(documentResponse.map((x) => x.documentId));
    },

    async createKPModelsFromDocument(context, param: CreateDocumentResponse[]) {
      const { rootDispatch, commit } = _moduleActionContext(context);
      const array: CreateDocumentCashDocumentModel[] = [];
      const cashbox = await rootDispatch.cashboxes.getDefaultUserCashbox();

      for (const createDocumentResponseItem of param.filter((v) => v.generateKP)) {
        const model = new CreateDocumentCashDocumentModel();
        model.cashboxId = cashbox?.id ?? null;
        model.issueDate = dayjs(createDocumentResponseItem.issueDate).format(DEFAULT_DATE_FORMAT);
        model.description = "";
        model.amount = null;
        model.documentType =
          createDocumentResponseItem.grossInCurrency > 0
            ? DOCUMENT_TYPES.CashIn
            : createDocumentResponseItem.grossInCurrency < 0
            ? DOCUMENT_TYPES.CashOut
            : null;
        model.contractor = createDocumentResponseItem.contractor;
        const cashDocumentItemEdit = new CashDocumentItemEdit();
        cashDocumentItemEdit.documentId = createDocumentResponseItem.documentId;
        cashDocumentItemEdit.name = createDocumentResponseItem.documentNumber;
        cashDocumentItemEdit.amountInCurrency = createDocumentResponseItem.grossInCurrency;
        model.documentItems = [cashDocumentItemEdit];
        const exchangeRate = await rootDispatch.exchangeRate.getLastExchangeRate({
          exchangeDate: model.issueDate,
          currency: cashbox?.currency ?? "PLN",
          skipAlert: true,
        });
        model.exchangeDate = exchangeRate.exchangeDate;
        model.exchangeRate = exchangeRate.exchangeRateValue;
        array.push(model);
      }
      commit.setKpKwModelsAfterDocumentCreate(array);
    },

    async askUserSettleWholeDocument(context) {
      const { commit, rootDispatch } = _moduleActionContext(context);
      const confirmModel = new ConfirmModel(
        filterCapitalize(i18n.t("doYouReallyWantToSettleWholeDocument")),
        filterCapitalize(i18n.t("warning")),
        [
          {
            text: filterCapitalize(i18n.t("no")),
            color: "warning",
            value: false,
            outlined: true,
          },
          {
            text: filterCapitalize(i18n.t("yes")),
            color: "warning",
            value: true,
          },
        ],
        "500px"
      );
      commit.setSettleWholeDocument((await rootDispatch.popup.addConfirm(confirmModel)) as boolean);
    },
    async askUserReceiptType(context) {
      const { commit, rootDispatch, rootState } = _moduleActionContext(context);
      // noinspection ES6MissingAwait
      await rootDispatch.downloadFiscalPrinters();
      if (rootState.fiscalPrinters !== null && rootState.fiscalPrinters.length > 0) {
        commit.setPrinterId(rootState.fiscalPrinters[0].id);
      }
      await new Promise<null>((resolve, reject) => {
        commit.setReceiptTypeDialogResolver({
          resolver: resolve,
          rejecter: reject,
        } as Resolver);
      });
      commit.setReceiptTypeDialogResolver(null);
    },
    async reprintDocument(context, orderDocument: OrderDocumentListItem) {
      const { rootDispatch, rootState, rootCommit } = _moduleActionContext(context);
      const result = await reprintDocument(
        {
          documentId: orderDocument.id,
          culture: orderDocument.translationCulture,
          layout: orderDocument.layout as string,
        },
        false
      );

      await DoubleConfirm.Process(result, rootDispatch.popup.addConfirm, async () => {
        await reprintDocument(
          {
            documentId: orderDocument.id,
            culture: orderDocument.translationCulture,
            layout: orderDocument.layout as string,
          },
          true
        );
      });

      await rootDispatch.popup.addSavedOkSnackbar();
      const selectedOrderId = rootState.orders.selectedOrderId;
      // noinspection ES6MissingAwait
      await rootDispatch.orders.getOrdersTable();
      rootCommit.orders.setSelectedOrderId(selectedOrderId);
      rootDispatch.mails.openNotificationDialog([orderDocument.id]);
      // noinspection ES6MissingAwait
      rootDispatch.orders.getOrderDocumentsTable();
    },
    async printProforma(context, orderId: string) {
      const { rootDispatch } = _moduleActionContext(context);
      await createDocument(orderId, {
        createPayments: false,
        printInvoice: false,
        printProformaInvoice: true,
        printProformaInvoiceGroup: false,
        printReceipt: false,
        printerId: null,
      });
      await rootDispatch.popup.addSavedOkSnackbar();
      // noinspection ES6MissingAwait
      rootDispatch.orders.getOrdersTable();
      // noinspection ES6MissingAwait
      rootDispatch.orders.getOrderDocumentsTable();
    },
    async printProformaGroup(context, orderId: string) {
      const { rootDispatch } = _moduleActionContext(context);
      await createDocument(orderId, {
        createPayments: false,
        printInvoice: false,
        printProformaInvoice: false,
        printProformaInvoiceGroup: true,
        printReceipt: false,
        printerId: null,
      });
      await rootDispatch.popup.addSavedOkSnackbar();
      // noinspection ES6MissingAwait
      rootDispatch.orders.getOrdersTable();
      // noinspection ES6MissingAwait
      rootDispatch.orders.getOrderDocumentsTable();
    },
  },
});

export default OrderCreateDocumentModule;

/* eslint-disable */
// noinspection JSUnusedLocalSymbols
const _moduleGetterContext = (args: [any, any, any, any]) =>
  moduleGetterContext(args, OrderCreateDocumentModule);
const _moduleActionContext = (context: any) =>
  moduleActionContext(context, OrderCreateDocumentModule);
